import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Camera, Baby, Heart, Church, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';

const serviceImages = {
    family: '/images/family.jpg',
    newborn: '/images/newborn.jpg',
    maternity: '/images/pregnancy.jpg',
  baptism: '/images/baptism.jpg',
};

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: 'easeOut' },
  viewport: { once: true }
};

export default function Services() {
  const { t } = useLanguage();

  const services = [
    { key: 'family', icon: Camera },
    { key: 'newborn', icon: Baby },
    { key: 'maternity', icon: Heart },
    { key: 'baptism', icon: Church },
  ];

  return (
    <div data-testid="services-page" className="pt-24 bg-warm-50">
      {/* Header */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div {...fadeUp} className="text-center">
            <span className="text-accent font-medium text-sm tracking-wider uppercase mb-3 block">
              {t.services.subtitle}
            </span>
            <h1 className="font-heading text-4xl md:text-5xl font-semibold text-warm-900">
              {t.services.title}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Services List */}
      <section className="bg-warm-50">
        {services.map((service, index) => {
          const Icon = service.icon;
          const serviceData = t.services[service.key];
          const isEven = index % 2 === 0;

          return (
            <motion.div
              key={service.key}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              data-testid={`service-section-${service.key}`}
              className={`py-14 md:py-20 ${isEven ? 'bg-warm-50' : 'bg-white'}`}
            >
              <div className="max-w-7xl mx-auto px-6">
                <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center`}>
                  {/* Image */}
                  <motion.div
                    initial={{ opacity: 0, x: isEven ? -30 : 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    viewport={{ once: true }}
                    className={`relative overflow-hidden rounded-2xl group ${isEven ? '' : 'lg:order-2'}`}
                  >
                    <div className="aspect-[4/3] overflow-hidden rounded-2xl shadow-lg">
                      <img
                        src={serviceImages[service.key]}
                        alt={serviceData.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                  </motion.div>

                  {/* Content */}
                  <motion.div
                    initial={{ opacity: 0, x: isEven ? 30 : -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    viewport={{ once: true }}
                    className={isEven ? '' : 'lg:order-1'}
                  >
                    <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center mb-6">
                      <Icon className="w-6 h-6 text-accent" />
                    </div>

                    <h2 className="font-heading text-2xl md:text-4xl font-semibold text-warm-900 mb-5">
                      {serviceData.title}
                    </h2>

                    <p className="text-warm-600 text-lg leading-relaxed mb-8">
                      {serviceData.description}
                    </p>

                    <Link to="/contact#booking" data-testid={`book-${service.key}`}>
                      <Button className="bg-accent hover:bg-accent-dark text-white font-medium px-8 py-3 rounded-full transition-all duration-300 hover:shadow-lg">
                        {t.nav.book}
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </section>
    </div>
  );
}
